import { db, functions } from '@/firebase-config';
import { getEffectiveUserId } from '@/features/auth/current-user-identity';
import type { ProjectCommunicationAudienceConfig } from '@/features/email/audience';
import {
  normalizeCommunicationSettings,
  normalizeEditableCommunicationSettings,
  type ProjectCommunicationSettings,
} from '@/features/email/communication-settings';
import { normalizeProjectCommunicationAudiences } from '@/features/sync/project-communication-normalizers';
import { asString, type ProjectDataRecord } from '@/features/sync/project-data-shape';
import {
  buildProjectCollectionPath,
  extractProjectScopeFromMemberPath,
  requireProjectScope,
  type ProjectScope,
} from '@/features/sync/project-scope';
import { createAndStoreProjectWriteIntent } from '@/features/sync/project-write-operation-factory';
import type { CloudProjectMeta } from '@/store/cloud-types';
import { logBetaAnalyticsEvent } from '@/utils/beta-analytics';
import { sanitizeDocumentForFirestore } from '@/utils/firestore-sanitizer';
import { logger } from '@/utils/logger';
import {
  collection,
  collectionGroup,
  documentId,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import type {
  CreateProjectOptions,
  OrgMembership,
  OrgMembershipClient,
  ProjectCatalogClient,
  ProjectCreateData,
} from '../contracts';
import { timestampDate } from './firestore-read-normalizers';

type ProjectDocumentSnapshot = {
  id: string;
  data: () => unknown;
};

type MemberDocumentSnapshot = {
  ref: { path: string };
};

type CreateProjectBootstrapRequest = {
  orgId: string;
  projectData: ProjectCreateData;
  isDemo: boolean;
};

type CreateProjectBootstrapResponse = {
  projectId: string;
};

const isSoftDeletedProjectRoot = (data: ProjectDataRecord): boolean => (
  data.deleted === true || data.status === 'deleted' || data.deletedAt != null
);

function normalizeOrgId(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function activeMembershipOrgIds(memberships: OrgMembership[]): Set<string> {
  return new Set(
    memberships
      .filter(membership => membership.status === 'active')
      .map(membership => membership.orgId)
      .filter(Boolean)
  );
}

const chunkArray = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const formatProjectDiscoveryError = (reason: unknown): string => (
  reason instanceof Error ? reason.message : String(reason)
);

export class FirebaseProjectCatalogClient implements ProjectCatalogClient {
  private pendingUserProjectsRequest: Promise<CloudProjectMeta[]> | null = null;
  private userProjectsCacheUserId: string | null = null;
  private readonly orgMemberships: OrgMembershipClient;

  constructor(orgMemberships: OrgMembershipClient) {
    this.orgMemberships = orgMemberships;
  }

  clearUserProjectsCache(): void {
    this.pendingUserProjectsRequest = null;
    this.userProjectsCacheUserId = null;
  }

  async createProject(
    userId: string,
    projectData: ProjectCreateData,
    options?: CreateProjectOptions,
  ): Promise<string> {
    try {
      const explicitOrgId = normalizeOrgId(options?.orgId);
      const orgId = explicitOrgId ?? await this.resolveProjectCreationOrgId(userId);
      const createProject = httpsCallable<CreateProjectBootstrapRequest, CreateProjectBootstrapResponse>(
        functions,
        'createProjectBootstrap'
      );
      const result = await createProject({
        orgId,
        projectData,
        isDemo: options?.isDemo === true,
      });
      const projectId = result.data.projectId;
      if (!projectId) {
        throw new Error('Project creation did not return a project id.');
      }
      logger.cloud('Created sharded project:', projectId);
      logBetaAnalyticsEvent('beta_project_created', {
        source: options?.isDemo ? 'demo' : 'manual',
        is_demo: options?.isDemo === true,
      });
      this.clearUserProjectsCache();
      return projectId;
    } catch (error: unknown) {
      logger.error('Error creating cloud project: ', error);
      throw error;
    }
  }

  async listUserProjects(userId: string): Promise<CloudProjectMeta[]> {
    try {
      if (!userId) throw new Error('User ID required');

      if (this.pendingUserProjectsRequest && this.userProjectsCacheUserId === userId) {
        logger.cloud(`Returning cached pending request for user: ${userId}`);
        return this.pendingUserProjectsRequest;
      }

      if (this.userProjectsCacheUserId !== userId) {
        this.pendingUserProjectsRequest = null;
      }

      this.userProjectsCacheUserId = userId;
      this.pendingUserProjectsRequest = this.fetchUserProjects(userId);

      const result = await this.pendingUserProjectsRequest;
      setTimeout(() => {
        if (this.userProjectsCacheUserId === userId) {
          this.pendingUserProjectsRequest = null;
          this.userProjectsCacheUserId = null;
        }
      }, 1000);

      return result;
    } catch (error: unknown) {
      this.pendingUserProjectsRequest = null;
      this.userProjectsCacheUserId = null;
      logger.error('Error fetching user projects: ', error);
      throw error;
    }
  }

  async deleteProject(scope: ProjectScope): Promise<void> {
    try {
      const checkedScope = requireProjectScope(scope);
      const deleteProjectPermanently = httpsCallable<ProjectScope, { success: true }>(
        functions,
        'deleteProjectPermanently'
      );
      await deleteProjectPermanently(checkedScope);
      this.clearUserProjectsCache();

      logger.cloud(`Project ${checkedScope.projectId} deletion requested.`);
    } catch (error: unknown) {
      logger.error('Error deleting cloud project:', error);
      throw error;
    }
  }

  async archiveProject(scope: ProjectScope): Promise<void> {
    try {
      await this.enqueueProjectRootMetadataWrite(scope, {
        status: 'archived',
        archivedAtMs: Date.now(),
      });
      this.clearUserProjectsCache();
      logger.cloud(`Archived project: ${scope.projectId}`);
    } catch (error: unknown) {
      logger.error('Error archiving project:', error);
      throw error;
    }
  }

  async unarchiveProject(scope: ProjectScope): Promise<void> {
    try {
      await this.enqueueProjectRootMetadataWrite(scope, {
        status: 'active',
        archivedAt: null,
      });
      this.clearUserProjectsCache();
      logger.cloud(`Unarchived project: ${scope.projectId}`);
    } catch (error: unknown) {
      logger.error('Error unarchiving project:', error);
      throw error;
    }
  }

  async updateProjectClosingDate(scope: ProjectScope, closingDate: string | null): Promise<void> {
    try {
      await this.enqueueProjectRootMetadataWrite(scope, {
        closingDate: closingDate || null,
      });
      this.clearUserProjectsCache();
      logger.cloud(`Updated closing date for project ${scope.projectId}: ${closingDate}`);
    } catch (error: unknown) {
      logger.error('Error updating project closing date:', error);
      throw error;
    }
  }

  async updateProjectCommunicationAudiences(
    scope: ProjectScope,
    config: ProjectCommunicationAudienceConfig,
  ): Promise<void> {
    try {
      await this.enqueueProjectRootMetadataWrite(scope, {
        communicationAudiences: normalizeProjectCommunicationAudiences(config),
      }, 'MANAGE_COMMUNICATIONS');
      logger.cloud(`Updated communication audiences for project ${scope.projectId}`);
    } catch (error: unknown) {
      logger.error('Error updating communication audiences:', error);
      throw error;
    }
  }

  async updateProjectCommunicationSettings(
    scope: ProjectScope,
    settings: ProjectCommunicationSettings,
  ): Promise<void> {
    try {
      await this.enqueueProjectRootMetadataWrite(scope, {
        communicationSettings: normalizeEditableCommunicationSettings(settings),
      }, 'MANAGE_COMMUNICATIONS');
      logger.cloud(`Updated communication settings for project ${scope.projectId}`);
    } catch (error: unknown) {
      logger.error('Error updating communication settings:', error);
      throw error;
    }
  }

  private async resolveProjectCreationOrgId(userId: string, requestedOrgId?: string | null): Promise<string> {
    const memberships = await this.orgMemberships.listUserOrgMemberships(userId);
    const membershipOrgIds = activeMembershipOrgIds(memberships);
    const explicitOrgId = normalizeOrgId(requestedOrgId);

    if (explicitOrgId) {
      if (membershipOrgIds.has(explicitOrgId)) {
        return explicitOrgId;
      }
      throw new Error('You must belong to the selected organization before creating a project there.');
    }

    const preferredOrgId = await this.orgMemberships.getActiveOrgIdPreference(userId);
    if (preferredOrgId && membershipOrgIds.has(preferredOrgId)) {
      return preferredOrgId;
    }

    if (membershipOrgIds.size === 1) {
      return Array.from(membershipOrgIds)[0]!;
    }

    throw new Error('Select an organization before creating a cloud project.');
  }

  private async fetchUserProjects(userId: string): Promise<CloudProjectMeta[]> {
    const memberships = await this.orgMemberships.listUserOrgMemberships(userId);
    const adminOrgIds = Array.from(new Set(
      memberships
        .filter(membership => membership.status === 'active' && (
          membership.role === 'owner' || membership.role === 'admin'
        ))
        .map(membership => membership.orgId)
    ));

    const qShared = query(
      collectionGroup(db, 'members'),
      where('userId', '==', userId),
      where('status', '==', 'active')
    );

    logger.cloud(`Fetching projects for user: ${userId}`);
    const [orgProjectsResults, memberProjectsResult] = await Promise.all([
      Promise.allSettled(
        adminOrgIds.map(orgId =>
          getDocs(collection(db, buildProjectCollectionPath(orgId)))
            .then(snapshot => ({ orgId, snapshot }))
        )
      ),
      Promise.resolve(getDocs(qShared)).then(
        value => ({ status: 'fulfilled' as const, value }),
        (reason: unknown) => ({ status: 'rejected' as const, reason })
      ),
    ]);

    const allOrgQueriesRejected = orgProjectsResults.length > 0
      && orgProjectsResults.every(result => result.status === 'rejected');
    if ((orgProjectsResults.length === 0 || allOrgQueriesRejected) && memberProjectsResult.status === 'rejected') {
      logger.warn(
        'Project discovery failed for both organization and shared project queries',
        `org=${orgProjectsResults.map(result => result.status === 'rejected' ? formatProjectDiscoveryError(result.reason) : '').filter(Boolean).join('; ')}`,
        `shared=${formatProjectDiscoveryError(memberProjectsResult.reason)}`
      );
      const firstOrgFailure = orgProjectsResults.find(result => result.status === 'rejected');
      throw firstOrgFailure?.status === 'rejected' && firstOrgFailure.reason instanceof Error
        ? firstOrgFailure.reason
        : new Error('Project discovery failed');
    }

    for (const result of orgProjectsResults) {
      if (result.status === 'rejected') {
        logger.warn('Organization project discovery failed; continuing with shared memberships', result.reason);
      }
    }

    if (memberProjectsResult.status === 'rejected') {
      logger.warn('Shared project discovery failed; continuing with organization projects', memberProjectsResult.reason);
    }

    return this.mergeDiscoveredProjects(userId, orgProjectsResults, memberProjectsResult);
  }

  private async mergeDiscoveredProjects(
    userId: string,
    orgProjectsResults: PromiseSettledResult<{ orgId: string; snapshot: { docs: ProjectDocumentSnapshot[] } }>[],
    memberProjectsResult: PromiseSettledResult<{ docs: MemberDocumentSnapshot[] }>,
  ): Promise<CloudProjectMeta[]> {
    const projectsMap = new Map<string, CloudProjectMeta>();
    const projectKey = (scope: ProjectScope): string => `${scope.orgId}/${scope.projectId}`;

    const processDoc = (docSnap: ProjectDocumentSnapshot, orgId: string) => {
      const data = docSnap.data() as ProjectDataRecord | undefined;
      const scope = { orgId, projectId: docSnap.id };
      const key = projectKey(scope);
      if (data && !isSoftDeletedProjectRoot(data) && !projectsMap.has(key)) {
        const ownerId = asString(data.ownerId);
        projectsMap.set(key, {
          orgId,
          id: docSnap.id,
          title: asString(data.title, asString(data.showTitle, 'Untitled')),
          updatedAt: timestampDate(data.updatedAt),
          ownerId,
          isShared: ownerId !== userId,
          status: data.status === 'archived' ? 'archived' : 'active',
          archivedAt: timestampDate(data.archivedAt),
          closingDate: typeof data.closingDate === 'string' ? data.closingDate : null,
          isBeta: data.isBeta === true,
          scriptTextAccess: data.scriptTextAccess === true,
          communicationAudiences: normalizeProjectCommunicationAudiences(data.communicationAudiences),
          communicationSettings: normalizeCommunicationSettings(data.communicationSettings),
        });
      }
    };

    for (const result of orgProjectsResults) {
      if (result.status === 'fulfilled') {
        for (const docSnap of result.value.snapshot.docs) {
          processDoc(docSnap, result.value.orgId);
        }
      }
    }

    const sharedProjectScopesByOrg = new Map<string, Set<string>>();
    if (memberProjectsResult.status === 'fulfilled') {
      for (const memberDoc of memberProjectsResult.value.docs) {
        const scope = extractProjectScopeFromMemberPath(memberDoc.ref.path);
        if (scope && !projectsMap.has(projectKey(scope))) {
          const projectIds = sharedProjectScopesByOrg.get(scope.orgId) ?? new Set<string>();
          projectIds.add(scope.projectId);
          sharedProjectScopesByOrg.set(scope.orgId, projectIds);
        }
      }
    }

    const sharedProjectQueries = await Promise.allSettled(
      Array.from(sharedProjectScopesByOrg.entries()).flatMap(([orgId, projectIds]) =>
        chunkArray(Array.from(projectIds), 30).map(projectIdChunk =>
          getDocs(query(
            collection(db, buildProjectCollectionPath(orgId)),
            where(documentId(), 'in', projectIdChunk)
          )).then(snapshot => ({ orgId, snapshot }))
        )
      )
    );
    for (const sharedProjectSnap of sharedProjectQueries) {
      if (sharedProjectSnap.status === 'fulfilled') {
        for (const docSnap of sharedProjectSnap.value.snapshot.docs) {
          processDoc(docSnap, sharedProjectSnap.value.orgId);
        }
      } else {
        logger.warn('Shared project document lookup failed; continuing with discovered projects', sharedProjectSnap.reason);
      }
    }

    return [...projectsMap.values()].toSorted((a, b) => {
      const dateA = a.updatedAt ? a.updatedAt.getTime() : Date.now();
      const dateB = b.updatedAt ? b.updatedAt.getTime() : Date.now();
      return dateB - dateA;
    });
  }

  private requireProjectMetadataActorId(): string {
    const actorId = getEffectiveUserId();
    if (!actorId) {
      throw new Error('Authentication required to preserve project metadata writes');
    }
    return actorId;
  }

  private async enqueueProjectRootMetadataWrite(
    scope: ProjectScope,
    payload: Record<string, unknown>,
    capability = 'EDIT_PROJECT_METADATA',
  ): Promise<void> {
    await createAndStoreProjectWriteIntent({
      scope,
      actorId: this.requireProjectMetadataActorId(),
      feature: 'setup_access',
      capability,
      kind: 'patchEntity',
      conflictUnit: 'project.rootMetadata',
      payload: sanitizeDocumentForFirestore(payload),
    });
  }
}
